import React, {useRef, useState} from 'react';
import {ActivityIndicator, StyleSheet, Text, TouchableOpacity, View} from 'react-native';

import {BookOpen, MessageSquare, Mic} from 'components/Icons';
import {returnHere, goTo, topUpCheckout} from 'utils/billing/billingApi';
import {ALL_THREE, TOPUP_PRESETS, TOPUP_RULES, meterMinor, money, quoteTopUp} from 'utils/billing/billingModel';
import UsageSlider from './UsageSlider';
import {showWorldNotice} from './worldNotice';

import { TYPE, FONTS } from 'style/typography';

const ICONS = {voice: Mic, text: MessageSquare, course: BookOpen};
const same = (a, b) => a.voice === b.voice && a.text === b.text && a.course === b.course;

/**
 * "Buy more usage" in the world - the portal panel: presets with "All three" selected, fluid
 * sliders, one total, and Buy goes straight to Stripe Checkout with no confirmation of ours
 * (owner 2026-09-29). Stripe returns to this world and unit. Prices: Sol topup-premium.
 */
export default function BuyMoreUsagePanel() {
  const [sel, setSel] = useState(ALL_THREE);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false); // a double tap opens one checkout, not two
  const quote = quoteTopUp(sel);

  const buy = async () => {
    if (inFlight.current || quote.blocked) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const data = await topUpCheckout({voiceMinutes: sel.voice, chatMinutes: sel.text, courses: sel.course}, returnHere());
      if (!data?.url) throw new Error('No checkout address');
      goTo(data.url);
    } catch (error) {
      console.error('Could not start top-up checkout', {status: error?.response?.status || null});
      showWorldNotice({title: 'Could not open checkout', description: 'Please try again in a moment.', tone: 'danger'});
      setBusy(false);
      inFlight.current = false;
    }
  };

  return (
    <View>
      <View style={styles.presets}>
        {TOPUP_PRESETS.map(preset => {
          const on = same(sel, preset.value);
          return (
            <TouchableOpacity key={preset.label} style={[styles.preset, on && styles.presetOn]} onPress={() => setSel(preset.value)}>
              <Text style={[styles.presetText, on && styles.presetTextOn]}>
                {preset.label} · {money(quoteTopUp(preset.value).totalMinor)}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
      {Object.keys(TOPUP_RULES).map(meter => {
        const rule = TOPUP_RULES[meter];
        const Icon = ICONS[meter];
        return (
          <View key={meter} style={styles.meter}>
            <View style={styles.meterHead}>
              <View style={styles.meterName}>
                <Icon size={16} color="#C5E836" />
                <Text style={styles.meterLabel}>{rule.label}</Text>
              </View>
              <Text style={styles.meterValue}>
                +{sel[meter]} <Text style={styles.meterPrice}>{money(meterMinor(meter, sel[meter]))}</Text>
              </Text>
            </View>
            <UsageSlider
              label={rule.label}
              value={sel[meter]}
              max={rule.max}
              step={rule.step}
              onChange={value => setSel(current => ({...current, [meter]: value}))}
            />
          </View>
        );
      })}
      <View style={styles.footer}>
        <View>
          <Text style={styles.total}>{money(quote.totalMinor)}</Text>
          <Text style={[styles.note, quote.blocked && styles.blocked]}>{quote.blocked || 'Valid for 12 months'}</Text>
        </View>
        <TouchableOpacity style={[styles.buy, (quote.blocked || busy) && styles.disabled]} disabled={Boolean(quote.blocked) || busy} onPress={buy}>
          {busy ? <ActivityIndicator size="small" color="#10151F" style={styles.spinner} /> : null}
          <Text style={styles.buyText}>Buy · {money(quote.totalMinor)}</Text>
        </TouchableOpacity>
      </View>
      <Text style={styles.after}>Used after your plan’s minutes</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  presets: {flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center'},
  preset: {margin: 4, paddingVertical: 7, paddingHorizontal: 12, borderRadius: 999, borderWidth: 1, borderColor: 'rgba(197,232,54,0.5)'},
  presetOn: {backgroundColor: '#C5E836', borderColor: '#C5E836'},
  presetText: {
    color: '#E4E4E7',
    fontSize: TYPE.sm,
    fontFamily: FONTS.body,
    fontWeight: '600',
  },
  presetTextOn: {color: '#10151F'},
  meter: {marginTop: 20},
  meterHead: {flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'},
  meterName: {flexDirection: 'row', alignItems: 'center'},
  meterLabel: {
    marginLeft: 8,
    color: '#D4D4D8',
    fontSize: TYPE.sm,
    fontFamily: FONTS.body,
  },
  meterValue: {
    color: '#F6FBFF',
    fontSize: TYPE.sm,
    fontFamily: FONTS.body,
    fontWeight: '700',
  },
  meterPrice: {color: '#71717A', fontWeight: '400'},
  footer: {marginTop: 24, paddingTop: 16, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.1)', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'},
  total: {
    color: '#F6FBFF',
    fontSize: TYPE['2xl'],
    fontFamily: FONTS.heading,
    fontWeight: '800',
  },
  note: {
    color: '#71717A',
    fontSize: TYPE.xs,
    fontFamily: FONTS.body,
  },
  blocked: {color: '#FCD34D'},
  buy: {flexDirection: 'row', alignItems: 'center', paddingVertical: 11, paddingHorizontal: 20, borderRadius: 10, backgroundColor: '#C5E836'},
  disabled: {opacity: 0.4},
  spinner: {marginRight: 8},
  buyText: {
    color: '#10151F',
    fontSize: TYPE.sm,
    fontFamily: FONTS.body,
    fontWeight: '800',
  },
  after: {
    marginTop: 12,
    textAlign: 'center',
    color: '#71717A',
    fontSize: TYPE.xs,
    fontFamily: FONTS.body,
  },
});
